import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Alert } from "@/components/ui/Alert";
import { FieldRow, TextareaInput } from "@/components/ui/Field";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { searchOrders } from "@/lib/projectOverview";
import type { AuftragLookup } from "@/lib/material";
import {
  getAssignmentFeinplanung,
  updateAssignmentFeinplanung,
  type PlanungDayOrders,
  type PlanungFeinplanungMember,
  type PlanungOrderLink,
} from "@/lib/montageplanung";
import { splitOrderLinkLabel } from "./displayLabels";
import {
  enumerateDays,
  formatDateDe,
  isoWeekday,
  MONTH_NAMES,
  parseIso,
  toIso,
  WEEKDAY_SHORT,
} from "./dateUtils";

const ISO_WEEKDAY_LABELS: Record<number, string> = {
  1: "Montag",
  2: "Dienstag",
  3: "Mittwoch",
  4: "Donnerstag",
  5: "Freitag",
  6: "Samstag",
  7: "Sonntag",
};

function toAuftragLookup(o: {
  id: number;
  auftragsnummer?: string;
  kunde?: string;
  beschreibung?: string;
  jahr?: number;
  ort?: string;
}): AuftragLookup {
  return {
    id: o.id,
    auftragsnummer: o.auftragsnummer?.trim() || String(o.id),
    jahr: o.jahr ?? null,
    kunde: o.kunde ?? null,
    ort: o.ort ?? null,
    beschreibung: o.beschreibung ?? null,
    ort_zusatz: null,
  };
}

function formatFinePlanningDayHeading(workDate: string): string {
  const date = parseIso(workDate);
  const weekday = ISO_WEEKDAY_LABELS[isoWeekday(date)] ?? "";
  return `${weekday}, ${formatDateDe(workDate)}`;
}

interface DaySnapshot {
  orders: PlanungOrderLink[];
  note: string | null;
}

function dayKey(workDate: string): string {
  return workDate.slice(0, 10);
}

function snapshotFromDay(day: PlanungDayOrders): DaySnapshot {
  return {
    orders: day.orders,
    note: day.note && day.note.trim() ? day.note : null,
  };
}

function dayFromSnapshot(iso: string, snapshot: DaySnapshot | undefined): PlanungDayOrders {
  return {
    work_date: iso,
    orders: snapshot?.orders ?? [],
    note: snapshot?.note ?? null,
  };
}

function mapFromDays(days: PlanungDayOrders[]): Map<string, DaySnapshot> {
  const map = new Map<string, DaySnapshot>();
  for (const day of days) {
    map.set(dayKey(day.work_date), snapshotFromDay(day));
  }
  return map;
}

function daysForRange(
  dateFrom: string,
  dateTo: string,
  edits: Map<string, DaySnapshot>,
): PlanungDayOrders[] {
  const from = parseIso(dateFrom);
  const to = parseIso(dateTo);
  return enumerateDays(from, to).map((date) => {
    const iso = toIso(date);
    return dayFromSnapshot(iso, edits.get(iso));
  });
}

function inRange(iso: string, from: string, to: string): boolean {
  return iso >= from && iso <= to;
}

function memberRange(member: PlanungFeinplanungMember): { from: string; to: string } {
  return {
    from: member.date_from.slice(0, 10),
    to: member.date_to.slice(0, 10),
  };
}

function DayMark({ iso }: { iso: string }) {
  const date = parseIso(iso);
  return (
    <div className="w-11 shrink-0 pt-0.5 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
        {WEEKDAY_SHORT[date.getDay()]}
      </div>
      <div className="mt-0.5 text-xl font-semibold leading-none tabular-nums text-label">
        {date.getDate()}
      </div>
    </div>
  );
}

const pagerButtonClass =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-label-secondary ring-1 ring-inset ring-separator transition hover:bg-fill-tertiary hover:text-label disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

export interface FinePlanningPanelState {
  dirty: boolean;
  loading: boolean;
  canEdit: boolean;
  datesOutOfSync: boolean;
}

export interface FinePlanningPanelHandle {
  /**
   * Schreibt die Feinplanung. `null`, wenn nichts zu speichern war.
   * `range` ist der Zeitraum, der gerade am Einsatz gespeichert wurde.
   */
  save: (range?: { dateFrom: string; dateTo: string }) => Promise<PlanungOrderLink[] | null>;
}

export interface FinePlanningPanelProps {
  assignmentId: number;
  active: boolean;
  /** Gespeicherter Zeitraum. Weicht das Formular ab, gilt das nur für einzelne Einsätze. */
  savedDateFrom?: string;
  savedDateTo?: string;
  /** Aktueller Zeitraum aus dem Einsatz-Formular. */
  draftDateFrom?: string;
  draftDateTo?: string;
  /** Aufträge aus dem Einsatz-Formular, die sich Tagen zuordnen lassen. */
  extraOrders?: PlanungOrderLink[];
  /**
   * Einzelner Einsatz: die Tage folgen dem Formular-Zeitraum.
   * Verknüpfte Termine zeigen weiterhin die gemeinsame Feinplanung.
   */
  followDraftRange?: boolean;
  onStateChange?: (state: FinePlanningPanelState) => void;
}

export const FinePlanningPanel = forwardRef<FinePlanningPanelHandle, FinePlanningPanelProps>(
  function FinePlanningPanel(
    {
      assignmentId,
      active,
      savedDateFrom,
      savedDateTo,
      draftDateFrom,
      draftDateTo,
      extraOrders,
      followDraftRange = false,
      onStateChange,
    },
    ref,
  ) {
    const [edits, setEdits] = useState<Map<string, DaySnapshot>>(new Map());
    const [baseline, setBaseline] = useState<Map<string, DaySnapshot>>(new Map());
    const [linkedDayIsos, setLinkedDayIsos] = useState<string[] | null>(null);
    const [assignmentOrders, setAssignmentOrders] = useState<PlanungOrderLink[]>([]);
    const [loadedRange, setLoadedRange] = useState<{ from: string; to: string } | null>(null);
    const [loading, setLoading] = useState(active);
    const [error, setError] = useState<string | null>(null);
    const [activeSearchDay, setActiveSearchDay] = useState<string | null>(null);
    const [orderQuery, setOrderQuery] = useState("");
    const [orderResults, setOrderResults] = useState<AuftragLookup[]>([]);
    const [orderSearching, setOrderSearching] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [linkedAssignmentIds, setLinkedAssignmentIds] = useState<number[]>([]);
    const [seriesMode, setSeriesMode] = useState(false);
    const [seriesMembers, setSeriesMembers] = useState<PlanungFeinplanungMember[]>([]);
    const [focusedMemberId, setFocusedMemberId] = useState(assignmentId);
    const [focusedAssignmentOrders, setFocusedAssignmentOrders] = useState<PlanungOrderLink[]>([]);
    const searchTimer = useRef<number | null>(null);
    const pagerRef = useRef<HTMLDivElement>(null);
    const pagerScrollTick = useRef(0);
    const onStateChangeRef = useRef(onStateChange);
    onStateChangeRef.current = onStateChange;
    const stateRef = useRef({ edits, baseline, linkedDayIsos, loadedRange, canEdit, seriesMode });
    stateRef.current = { edits, baseline, linkedDayIsos, loadedRange, canEdit, seriesMode };

    const isLinked = linkedDayIsos != null;

    const draftReady = Boolean(
      draftDateFrom && draftDateTo && draftDateTo >= draftDateFrom,
    );
    const useDraftRange = followDraftRange && !isLinked && draftReady;

    const datesOutOfSync = useMemo(() => {
      if (isLinked || useDraftRange) return false;
      if (!loadedRange || !savedDateFrom || !savedDateTo) return false;
      return (
        savedDateFrom.slice(0, 10) !== loadedRange.from ||
        savedDateTo.slice(0, 10) !== loadedRange.to
      );
    }, [isLinked, useDraftRange, loadedRange, savedDateFrom, savedDateTo]);

    const focusedMember = useMemo(() => {
      if (!seriesMode || seriesMembers.length < 2) return null;
      return (
        seriesMembers.find((member) => member.id === focusedMemberId) ??
        seriesMembers.find((member) => member.id === assignmentId) ??
        seriesMembers[0] ??
        null
      );
    }, [seriesMode, seriesMembers, focusedMemberId, assignmentId]);

    const days = useMemo(() => {
      if (focusedMember) {
        const range = memberRange(focusedMember);
        return daysForRange(range.from, range.to, edits);
      }
      if (useDraftRange && draftDateFrom && draftDateTo) {
        return daysForRange(draftDateFrom, draftDateTo, edits);
      }
      if (isLinked && linkedDayIsos) {
        return linkedDayIsos.map((iso) => dayFromSnapshot(iso, edits.get(iso)));
      }
      if (loadedRange) {
        return daysForRange(loadedRange.from, loadedRange.to, edits);
      }
      return [];
    }, [
      focusedMember,
      useDraftRange,
      draftDateFrom,
      draftDateTo,
      edits,
      isLinked,
      linkedDayIsos,
      loadedRange,
    ]);

    const dirty = useMemo(() => {
      if (!canEdit || loading || !loadedRange) return false;
      if (isLinked && linkedDayIsos) {
        const current = linkedDayIsos.map((iso) => dayFromSnapshot(iso, edits.get(iso)));
        const base = linkedDayIsos.map((iso) => dayFromSnapshot(iso, baseline.get(iso)));
        return hasFormChanges(current, base);
      }
      const from = useDraftRange && draftDateFrom ? draftDateFrom : loadedRange.from;
      const to = useDraftRange && draftDateTo ? draftDateTo : loadedRange.to;
      const current = daysForRange(from, to, edits);
      const base = daysForRange(loadedRange.from, loadedRange.to, baseline);
      return hasFormChanges(current, base);
    }, [
      canEdit,
      loading,
      loadedRange,
      isLinked,
      linkedDayIsos,
      edits,
      baseline,
      useDraftRange,
      draftDateFrom,
      draftDateTo,
    ]);

    useEffect(() => {
      onStateChangeRef.current?.({ dirty, loading, canEdit, datesOutOfSync });
    }, [dirty, loading, canEdit, datesOutOfSync]);

    useEffect(() => {
      if (!active) {
        setLoading(false);
        return;
      }
      setError(null);
      setActiveSearchDay(null);
      setOrderQuery("");
      setOrderResults([]);
      setLoading(true);
      setCanEdit(false);
      setLinkedAssignmentIds([]);
      setSeriesMode(false);
      setSeriesMembers([]);
      setFocusedMemberId(assignmentId);
      setFocusedAssignmentOrders([]);
      setLinkedDayIsos(null);
      pagerScrollTick.current = 0;
      let cancelled = false;
      void getAssignmentFeinplanung(assignmentId)
        .then((data) => {
          if (cancelled) return;
          const from = data.date_from.slice(0, 10);
          const to = data.date_to.slice(0, 10);
          const nextEdits = mapFromDays(data.days);
          setLoadedRange({ from, to });
          setAssignmentOrders(data.assignment_orders ?? []);
          setEdits(nextEdits);
          setBaseline(mapFromDays(data.days));
          setCanEdit(!!data.can_edit);
          const peers = data.linked_assignment_ids ?? [];
          setLinkedAssignmentIds(peers);
          const nextSeriesMode = !!data.series_mode;
          setSeriesMode(nextSeriesMode);
          setSeriesMembers(nextSeriesMode ? (data.series_members ?? []) : []);
          setFocusedMemberId(assignmentId);
          setFocusedAssignmentOrders(
            data.focused_assignment_orders ?? data.assignment_orders ?? [],
          );
          setLinkedDayIsos(peers.length > 0 ? data.days.map((day) => dayKey(day.work_date)) : null);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : "Feinplanung konnte nicht geladen werden.");
          setEdits(new Map());
          setBaseline(new Map());
          setSeriesMembers([]);
          setFocusedAssignmentOrders([]);
          setLinkedDayIsos(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, [active, assignmentId]);

    useEffect(() => {
      if (!active || !activeSearchDay) return;
      const q = orderQuery.trim();
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
      if (q.length < 2) {
        setOrderResults([]);
        setOrderSearching(false);
        return;
      }
      setOrderSearching(true);
      searchTimer.current = window.setTimeout(async () => {
        try {
          const results = await searchOrders(q, 8);
          setOrderResults(results.map(toAuftragLookup));
        } catch {
          setOrderResults([]);
        } finally {
          setOrderSearching(false);
        }
      }, 250);
      return () => {
        if (searchTimer.current) window.clearTimeout(searchTimer.current);
      };
    }, [orderQuery, active, activeSearchDay]);

    function updateDay(workDate: string, recipe: (snapshot: DaySnapshot) => DaySnapshot) {
      const iso = dayKey(workDate);
      setEdits((prev) => {
        const next = new Map(prev);
        const current = next.get(iso) ?? { orders: [], note: null };
        next.set(iso, recipe(current));
        return next;
      });
    }

    function addOrderToDay(workDate: string, order: AuftragLookup) {
      updateDay(workDate, (snapshot) => {
        if (snapshot.orders.some((item) => item.order_id === order.id)) return snapshot;
        return {
          ...snapshot,
          orders: [
            ...snapshot.orders,
            { order_id: order.id, order_label: formatAuftragLookupLabel(order) },
          ],
        };
      });
      setOrderQuery("");
      setOrderResults([]);
      setActiveSearchDay(null);
    }

    function removeOrderFromDay(workDate: string, orderId: number) {
      updateDay(workDate, (snapshot) => ({
        ...snapshot,
        orders: snapshot.orders.filter((item) => item.order_id !== orderId),
      }));
    }

    function addAssignmentOrderToDay(workDate: string, link: PlanungOrderLink) {
      updateDay(workDate, (snapshot) => {
        if (snapshot.orders.some((item) => item.order_id === link.order_id)) return snapshot;
        return { ...snapshot, orders: [...snapshot.orders, link] };
      });
    }

    function updateDayNote(workDate: string, note: string) {
      updateDay(workDate, (snapshot) => ({ ...snapshot, note: note || null }));
    }

    function focusMember(id: number) {
      setFocusedMemberId(id);
      setActiveSearchDay(null);
      setOrderQuery("");
      setOrderResults([]);
      pagerScrollTick.current += 1;
    }

    useImperativeHandle(ref, () => ({
      async save(range) {
        const current = stateRef.current;
        if (!current.canEdit || !current.loadedRange) return null;
        const savedFrom = current.loadedRange.from;
        const savedTo = current.loadedRange.to;
        const ownFrom = savedDateFrom?.slice(0, 10);
        const ownTo = savedDateTo?.slice(0, 10);
        const nextFrom = range?.dateFrom?.slice(0, 10) || ownFrom || savedFrom;
        const nextTo = range?.dateTo?.slice(0, 10) || ownTo || savedTo;
        const linked = current.linkedDayIsos != null;

        let payloadDays: PlanungDayOrders[];
        if (linked && current.linkedDayIsos) {
          payloadDays = current.linkedDayIsos
            .filter((iso) => {
              if (!ownFrom || !ownTo) return true;
              const wasOnThisAssignment = inRange(iso, ownFrom, ownTo);
              const staysOnThisAssignment = inRange(iso, nextFrom, nextTo);
              if (wasOnThisAssignment && !staysOnThisAssignment) return false;
              return true;
            })
            .map((iso) => dayFromSnapshot(iso, current.edits.get(iso)));
          const baselineDays = current.linkedDayIsos.map((iso) =>
            dayFromSnapshot(iso, current.baseline.get(iso)),
          );
          const rangeUnchanged = !ownFrom || !ownTo || (nextFrom === ownFrom && nextTo === ownTo);
          if (rangeUnchanged && !hasFormChanges(payloadDays, baselineDays)) return null;
        } else {
          payloadDays = daysForRange(nextFrom, nextTo, current.edits);
          const baselineDays = daysForRange(savedFrom, savedTo, current.baseline);
          const unchanged =
            nextFrom === savedFrom &&
            nextTo === savedTo &&
            !hasFormChanges(payloadDays, baselineDays);
          if (unchanged) return null;
        }

        const result = await updateAssignmentFeinplanung(assignmentId, { days: payloadDays });
        const nextEdits = mapFromDays(result.days);
        setEdits(nextEdits);
        setBaseline(mapFromDays(result.days));
        setLoadedRange({
          from: result.date_from.slice(0, 10),
          to: result.date_to.slice(0, 10),
        });
        setAssignmentOrders(result.assignment_orders ?? []);
        const nextSeriesMode = !!result.series_mode;
        setSeriesMode(nextSeriesMode);
        setSeriesMembers(nextSeriesMode ? (result.series_members ?? []) : []);
        setFocusedAssignmentOrders(
          result.focused_assignment_orders ?? result.assignment_orders ?? [],
        );
        const peers = result.linked_assignment_ids ?? [];
        setLinkedAssignmentIds(peers);
        setLinkedDayIsos(peers.length > 0 ? result.days.map((day) => dayKey(day.work_date)) : null);
        return result.series_mode
          ? (result.focused_assignment_orders ?? [])
          : (result.assignment_orders ?? []);
      },
    }));

    useEffect(() => {
      if (pagerScrollTick.current === 0) return;
      pagerRef.current?.scrollIntoView({ block: "start" });
    }, [focusedMemberId]);

    const quickOrders = useMemo(() => {
      const map = new Map<number, PlanungOrderLink>();
      if (focusedMember) {
        if (focusedMember.id === assignmentId) {
          for (const order of focusedAssignmentOrders) map.set(order.order_id, order);
          for (const order of extraOrders ?? []) map.set(order.order_id, order);
        }
        for (const day of days) {
          for (const order of day.orders) map.set(order.order_id, order);
        }
      } else {
        for (const order of assignmentOrders) map.set(order.order_id, order);
        for (const order of extraOrders ?? []) map.set(order.order_id, order);
      }
      return [...map.values()];
    }, [
      focusedMember,
      assignmentId,
      focusedAssignmentOrders,
      extraOrders,
      days,
      assignmentOrders,
    ]);

    const focusIndex = focusedMember
      ? seriesMembers.findIndex((member) => member.id === focusedMember.id)
      : -1;
    const previousMember = focusIndex > 0 ? seriesMembers[focusIndex - 1] : null;
    const nextMember =
      focusIndex >= 0 && focusIndex < seriesMembers.length - 1
        ? seriesMembers[focusIndex + 1]
        : null;

    const plannedCount = useMemo(
      () => days.filter((day) => day.orders.length > 0 || !!day.note?.trim()).length,
      [days],
    );

    const monthBreaks = useMemo(() => {
      const marks = new Set<string>();
      let last = "";
      for (const day of days) {
        const iso = dayKey(day.work_date);
        const month = iso.slice(0, 7);
        if (month !== last) {
          marks.add(iso);
          last = month;
        }
      }
      return marks;
    }, [days]);

    const rangeLabel = focusedMember
      ? `${formatDateDe(focusedMember.date_from)} – ${formatDateDe(focusedMember.date_to)}`
      : useDraftRange && draftDateFrom && draftDateTo
        ? `${formatDateDe(draftDateFrom)} – ${formatDateDe(draftDateTo)}`
        : loadedRange
          ? `${formatDateDe(loadedRange.from)} – ${formatDateDe(loadedRange.to)}`
          : "";

    const draftDiffers =
      isLinked &&
      !!savedDateFrom &&
      !!savedDateTo &&
      !!draftDateFrom &&
      !!draftDateTo &&
      (draftDateFrom.slice(0, 10) !== savedDateFrom.slice(0, 10) ||
        draftDateTo.slice(0, 10) !== savedDateTo.slice(0, 10));

    return (
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}

        {datesOutOfSync && (
          <Alert variant="warning">
            Der Zeitraum im Einsatz wurde geändert, aber noch nicht gespeichert. Bitte speichern
            Sie den Einsatz zuerst, bevor Sie die Feinplanung bearbeiten.
          </Alert>
        )}

        {draftDiffers && (
          <Alert variant="info">
            Der neue Zeitraum gilt mit dem Speichern. Die Tage unten sind die aktuelle Feinplanung
            {focusedMember ? " dieses Einsatzes." : " der verknüpften Termine."}
          </Alert>
        )}

        {!loading && !canEdit && !error && (
          <Alert variant="info">Sie können diese Feinplanung nur ansehen.</Alert>
        )}

        {loading && <p className="text-sm text-label-secondary">Feinplanung wird geladen…</p>}

        {!loading && loadedRange && (
          <>
            {focusedMember ? (
              <div ref={pagerRef} className="flex items-center gap-2">
                <button
                  type="button"
                  className={pagerButtonClass}
                  disabled={!previousMember}
                  aria-label="Vorherigen Einsatz anzeigen"
                  onClick={() => previousMember && focusMember(previousMember.id)}
                >
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                </button>
                <div className="min-w-0 flex-1 text-center">
                  <p className="truncate text-xs font-medium text-label">{rangeLabel}</p>
                  <p className="text-[11px] text-label-tertiary">
                    {focusedMember.id === assignmentId
                      ? "Dieser Einsatz"
                      : focusedMember.title.trim() || "Weiterer Einsatz"}
                    {` · ${focusIndex + 1} von ${seriesMembers.length}`}
                  </p>
                  {focusedMember.id !== assignmentId && (
                    <button
                      type="button"
                      className="mt-0.5 text-[11px] font-medium text-accent hover:underline"
                      onClick={() => focusMember(assignmentId)}
                    >
                      Zu diesem Einsatz
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className={pagerButtonClass}
                  disabled={!nextMember}
                  aria-label="Nächsten Einsatz anzeigen"
                  onClick={() => nextMember && focusMember(nextMember.id)}
                >
                  <ChevronRightIcon className="h-4 w-4" aria-hidden />
                </button>
              </div>
            ) : (
              isLinked && (
                <p className="text-xs leading-relaxed text-label-secondary">
                  {seriesMode
                    ? `Serie · ${linkedAssignmentIds.length + 1} Termine. Ein Projekt gilt nur für den Tag, an dem Sie es eintragen.`
                    : `Verknüpft · ${linkedAssignmentIds.length + 1} Einsätze. Die Montage-Tage werden gemeinsam bearbeitet.`}
                </p>
              )
            )}

            <p className="text-sm text-label-secondary">
              {days.length === 0
                ? "Keine Tage in diesem Zeitraum."
                : focusedMember
                  ? plannedCount === 0
                    ? `${days.length} Tag${days.length === 1 ? "" : "e"} ohne Eintrag. Ein Projekt gilt nur für den Tag, an dem Sie es eintragen.`
                    : `${plannedCount} von ${days.length} Tagen geplant. Ein Projekt gilt nur für den Tag, an dem Sie es eintragen.`
                  : `${days.length} Tag${days.length === 1 ? "" : "e"}${rangeLabel ? ` · ${rangeLabel}` : ""}. Projekte und Notizen gelten für den jeweiligen Tag.`}
            </p>

            {canEdit && quickOrders.length > 0 && (
              <FieldRow
                label={seriesMode ? "Projekte zuordnen" : "Aufträge zuordnen"}
                hint="Ein Auftrag lässt sich auf den passenden Tag setzen."
              >
                <ul className="flex flex-wrap gap-1.5">
                  {quickOrders.map((order) => (
                    <li key={order.order_id}>
                      <span className="inline-block rounded-lg border border-separator-opaque bg-card px-2 py-1 text-xs text-label">
                        {order.order_label || `Auftrag ${order.order_id}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </FieldRow>
            )}

            <ol className="space-y-4">
              {days.map((day) => {
                const workDate = dayKey(day.work_date);
                const selectedIds = new Set(day.orders.map((order) => order.order_id));
                const showSearch = activeSearchDay === workDate;
                const showMonth = monthBreaks.has(workDate);
                const monthDate = parseIso(workDate);
                return (
                  <li key={workDate}>
                    {showMonth && (
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-label-tertiary">
                        {MONTH_NAMES[monthDate.getMonth()]} {monthDate.getFullYear()}
                      </p>
                    )}
                    <article className="flex gap-3">
                      <DayMark iso={workDate} />
                      <div className="min-w-0 flex-1 rounded-2xl bg-card px-3.5 py-3 shadow-sm ring-1 ring-inset ring-separator">
                    <h3 className="sr-only">{formatFinePlanningDayHeading(workDate)}</h3>
                    {canEdit && !datesOutOfSync && (
                      <div className="mb-2 flex flex-wrap justify-end gap-1">
                        {quickOrders
                          .filter((order) => !selectedIds.has(order.order_id))
                          .map((order) => (
                            <button
                              key={order.order_id}
                              type="button"
                              className="rounded-md border border-separator px-2 py-0.5 text-left text-[11px] text-label-secondary hover:bg-fill-tertiary hover:text-label"
                              onClick={() => addAssignmentOrderToDay(workDate, order)}
                            >
                              + {order.order_label || `Auftrag ${order.order_id}`}
                            </button>
                          ))}
                        <button
                          type="button"
                          className="rounded-md border border-separator px-2 py-0.5 text-[11px] text-tint hover:bg-fill-tertiary"
                          onClick={() => {
                            setActiveSearchDay(showSearch ? null : workDate);
                            setOrderQuery("");
                            setOrderResults([]);
                          }}
                        >
                          {showSearch ? "Suche schließen" : "Auftrag suchen…"}
                        </button>
                      </div>
                    )}

                    {day.orders.length > 0 ? (
                      <ul className="space-y-2">
                        {day.orders.map((order) => {
                          const { number, details } = splitOrderLinkLabel(
                            order.order_id,
                            order.order_label,
                          );
                          return (
                            <li
                              key={order.order_id}
                              className="flex items-start justify-between gap-2"
                            >
                              <div className="min-w-0">
                                <div className="font-mono text-[13px] font-semibold leading-tight tracking-tight text-label">
                                  {number}
                                </div>
                                {details ? (
                                  <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-label-secondary">
                                    {details}
                                  </div>
                                ) : null}
                              </div>
                              {canEdit && !datesOutOfSync ? (
                                <button
                                  type="button"
                                  className="shrink-0 text-xs text-status-red hover:underline"
                                  onClick={() => removeOrderFromDay(workDate, order.order_id)}
                                >
                                  Entfernen
                                </button>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-xs text-label-tertiary">Noch nichts für diesen Tag eingeplant.</p>
                    )}

                    {day.note?.trim() || (canEdit && !datesOutOfSync) ? (
                      <TextareaInput
                        label="Notiz zum Tag"
                        hint="Nur für diesen Tag, zum Beispiel Treffpunkt. Erscheint im Hover, nicht in den Montageunterlagen."
                        value={day.note ?? ""}
                        onChange={(event) => updateDayNote(workDate, event.target.value)}
                        className="mt-3"
                        rows={2}
                        readOnly={!canEdit || datesOutOfSync}
                      />
                    ) : null}

                    {showSearch && (
                      <div className="relative mt-2">
                        <input
                          className="eh-input"
                          value={orderQuery}
                          onChange={(event) => setOrderQuery(event.target.value)}
                          placeholder="Auftrag suchen und hinzufügen…"
                          autoFocus
                        />
                        {(orderSearching || orderResults.length > 0) && orderQuery.trim().length >= 2 && (
                          <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-separator-opaque bg-card shadow-ios-md">
                            {orderSearching && (
                              <div className="px-3 py-2 text-sm text-label-secondary">Suche…</div>
                            )}
                            {!orderSearching &&
                              orderResults
                                .filter((order) => !selectedIds.has(order.id))
                                .map((order) => (
                                  <button
                                    key={order.id}
                                    type="button"
                                    className="block w-full px-3 py-2 text-left text-sm line-clamp-2 break-words hover:bg-fill-tertiary"
                                    onClick={() => addOrderToDay(workDate, order)}
                                  >
                                    {formatAuftragLookupLabel(order)}
                                  </button>
                                ))}
                            {!orderSearching &&
                              orderResults.filter((order) => !selectedIds.has(order.id)).length === 0 && (
                                <div className="px-3 py-2 text-sm text-label-secondary">Keine Treffer</div>
                              )}
                          </div>
                        )}
                      </div>
                    )}
                      </div>
                    </article>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    );
  },
);
